import "./style.scss";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { SubmitHandler, useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import FormApplication from "../FormApplication/FormApplication";
import FormSummary from "../FormSummary/FormSummary";
import { FileFieldName, IApplyOnline } from "@/src/types/applyOnline";
import { applicationPageRequests } from "@/src/api/applicationPageRequests";
import { APPLY_ONLINE_DEFAULT_VALUES, isApplyOnlineFormReady } from "@/src/constants/apply-online";
import { RootState } from "@/src/store";

const EMPTY_FILES_MAP: Record<FileFieldName, File[]> = {
  filePassport: [],
  fileCertificate: [],
  fileEducation: [],
};

const serializeFilesForLog = (files?: File[]) =>
  files?.map((file) => ({ name: file.name, size: file.size, type: file.type })) ?? [];

const buildApplyOnlineSubmitPayload = (data: IApplyOnline, officeUrl: string) => ({
  fullname: data.fullname || "",
  office: data.office || "",
  phone: data.phone || "",
  email: data.email || "",
  office_url: officeUrl,
  birth_date: data.birthDate || "",
  nationality: data.nationality || "",
  country: data.country || "",
  address: data.address || "",
  passport_number: data.passportNumber || "",
  speciality: data.speciality || "",
  level_education: data.levelEducation || "",
  form_study: data.formStudy || "",
  text: data.comment || "",
  form_type: "APPLY_ONLINE",
  form_office: data.office || "",
  files_passport: serializeFilesForLog(data.filePassport),
  files_certificate: serializeFilesForLog(data.fileCertificate),
  files_education: serializeFilesForLog(data.fileEducation),
  files_another: serializeFilesForLog(data.fileAnouther),
});

interface IApplyOnlineFormProps {
  onSectionStatusChange: (status: Record<string, string>) => void;
  loadActualForm: () => void | Promise<void>;
  onCloseForm?: () => void;
}

const ApplyOnlineForm: React.FC<IApplyOnlineFormProps> = ({
  onSectionStatusChange,
  loadActualForm,
  onCloseForm,
}) => {
  const selectedSiteUrl = useSelector((state: RootState) => state.accessControl.selectedSiteUrl);
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    control,
    formState: { errors },
  } = useForm<IApplyOnline>({
    mode: "onChange",
    defaultValues: APPLY_ONLINE_DEFAULT_VALUES,
  });
  const [formData, setFormData] = useState<IApplyOnline>();
  const allFieldsWatch = useWatch({ control });
  const isSubmitDisabled = useMemo(
    () => !isApplyOnlineFormReady(allFieldsWatch, errors),
    [allFieldsWatch, errors]
  );

  const getStatus = useCallback((fields: (string | undefined)[]) => {
    const filled = fields.filter((f) => f && f.trim() !== "").length;
    if (filled === fields.length) return "full";
    if (filled > 0) return "not-enough";
    return "";
  }, []);

  const getAllStatusFields = useCallback((
    allFields: IApplyOnline,
    overrideFourStatus: string = ""
  ): Record<string, string> => ({
    one: getStatus([allFields.fullname, allFields.office, allFields.phone, allFields.email]),
    two: getStatus([
      allFields.birthDate,
      allFields.nationality,
      allFields.country,
      allFields.address,
    ]),
    three: getStatus([allFields.speciality, allFields.levelEducation, allFields.formStudy]),
    four: overrideFourStatus,
  }), [getStatus]);

  useEffect(() => {
    const statusFields = getAllStatusFields(allFieldsWatch);
    onSectionStatusChange(statusFields);
  }, [allFieldsWatch, getAllStatusFields, onSectionStatusChange]);

  useEffect(() => {
    setFormData(allFieldsWatch);
  }, [allFieldsWatch]);

  const onSubmit: SubmitHandler<IApplyOnline> = async (data) => {
    const officeUrl = selectedSiteUrl || "";
    const formData = new FormData();
    formData.append("fullname", data.fullname || "");
    formData.append("office", data.office || "");
    formData.append("phone", data.phone || "");
    formData.append("email", data.email || "");
    formData.append("office_url", officeUrl);

    formData.append("birth_date", data.birthDate || "");
    formData.append("nationality", data.nationality || "");
    formData.append("country", data.country || "");
    formData.append("address", data.address || "");
    formData.append("passport_number", data.passportNumber || "");

    formData.append("speciality", data.speciality || "");
    formData.append("level_education", data.levelEducation || "");
    formData.append("form_study", data.formStudy || "");

    formData.append("text", data.comment || "");

    if (data.filePassport?.length) {
      data.filePassport.forEach((file: File) => {
        formData.append("files_passport", file);
      });
    }

    if (data.fileCertificate?.length) {
      data.fileCertificate.forEach((file: File) => {
        formData.append("files_certificate", file);
      });
    }

    if (data.fileEducation?.length) {
      data.fileEducation.forEach((file: File) => {
        formData.append("files_education", file);
      });
    }

    if (data.fileAnouther?.length) {
      data.fileAnouther.forEach((file: File) => {
        formData.append("files_another", file);
      });
    }

    formData.append("form_type", "APPLY_ONLINE");
    formData.append("form_office", data.office || "");

    console.log("ApplyOnline submit payload:", buildApplyOnlineSubmitPayload(data, officeUrl));

    const isCreated = await applicationPageRequests.create(formData);
    if (!isCreated) {
      alert("Failed to submit the application. Please try again.");
      return;
    }

    reset(APPLY_ONLINE_DEFAULT_VALUES);
    setSelectedFilesMap(EMPTY_FILES_MAP);
    onSectionStatusChange({
      one: "",
      two: "",
      three: "",
      four: "",
    });
    onCloseForm?.();
    await loadActualForm();
  };

  const [selectedFilesMap, setSelectedFilesMap] = useState<Record<FileFieldName, File[]>>(EMPTY_FILES_MAP);

  return (
    <form className="apply-online-form">
      <FormApplication
        register={register}
        control={control}
        errors={errors}
        setValue={setValue}
        handleSubmit={() => handleSubmit(onSubmit)()}
        selectedFilesMap={selectedFilesMap}
        setSelectedFilesMap={setSelectedFilesMap}
        isSubmitDisabled={isSubmitDisabled}
      />
      <FormSummary
        register={register}
        control={control}
        errors={errors}
        setValue={setValue}
        handleSubmit={() => handleSubmit(onSubmit)()}
        selectedFilesMap={selectedFilesMap}
        setSelectedFilesMap={setSelectedFilesMap}
        formData={formData}
        isSubmitDisabled={isSubmitDisabled}
      />
    </form>
  );
};

export default ApplyOnlineForm;
